import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { PropTypes } from "prop-types";
import { availPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../../../config/api-config";
import columnConfig from "./ctTableConfig";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import TableStore from "@harbor/elements/utils/table/store";
import useSelectableRows from "@harbor/elements/utils/table/hooks/selectableRows";
import {
  generateRowID,
  payloadOnQueryUpdate,
  defaultRowID,
} from "../../../../common/adUtils";
import { downloadAsCSVFile } from "../../../../../../../../utils/common";
import ADTableView from "../../../../genericComponents/ADTableView";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import Spinner from "../../../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import css from "../../../../common/anomalyMagneticStyle.less";
import { addUnitsFromDate } from "../../../../../../../../utils/displayTime";
import useI18n from "../../../../../../../../i18n/useI18n";
import {
  AD_USAGE_CIRCUITS_AVAILABILITY_TABLE_DEFAULT_TGL,
  LOCAL_STORAGE_LABELS,
} from "../../../../../../../../utils/enums";
import { useTableSettings } from "../../../../../../../../utils/table";

const defaultSort = [["availability", "desc"]];
const tableSettingsId = "ad_usage_circuits_availability_table";

const CircuitTrendAvailTable = (props) => {
  const { tViews } = useI18n("views");
  const latestRequest = useRef(0);
  const tableStoreRef = useRef(null);
  const [tableLoader, setTableLoader] = useState(null);
  const [tableStore, setTableStore] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [resReceived, setResReceived] = useState(null);

  const openTableSettings = useCallback(() => {
    tableStoreRef.current?.setState({ settingsOpen: true });
  }, []);

  const columnsHook = useColumns(
    useMemo(
      () => ({
        config: ({ store }) => columnConfig({ store, openTableSettings }),
        order: defaultSort,
      }),
      [openTableSettings],
    ),
  );

  const selectableRowsHook = useSelectableRows(
    useMemo(
      () => ({
        selected: defaultRowID(),
      }),
      [],
    ),
  );

  const getSelectedRows = useCallback((rows, source) => {
    const circuitNames = source
      .filter((item) => rows.includes(item.id) && item.availability !== "-")
      .map((item) => {
        return { name: item.display_circuit_name, id: item.circuit_name };
      });
    props.saveSelectedTrendCircuits(circuitNames);
  }, []);

  useEffect(() => {
    const runEffect = async () => {
      const abortController = new AbortController();
      latestRequest.current += 1;
      const requestId = latestRequest.current;
      try {
        if (props.selectedHour && props.site) {
          setResReceived(false);
          let tableStore = new TableStore({
            query: { pageLength: 30 },
            hooks: [columnsHook, selectableRowsHook],
            fetchData: async ({ query, setSource, setCount }) => {
              if (!query.export) setTableLoader(true);
              let payload = availPayload(props.selectedHour, props.site);
              payload = payloadOnQueryUpdate(query, payload);
              let res = await ApiService.getAvailTableData(payload, {
                signal: abortController.signal,
              });
              if (latestRequest.current !== requestId) return;
              if (!res.errorObject && !query.export) {
                let tableData = generateRowID(res?.data?.data, false);
                setSource(tableData);
                setCount(res?.data?.count, false);
                setTableLoader(false);
                let processedRows = tableData.filter(
                  (item) => item.availability !== "-",
                );
                selectableRowsHook.setSelected(
                  defaultRowID(processedRows.length),
                );
                setResReceived(true);
              } else if (query.export) {
                let resData = res?.data?.data;
                let columns = tableStore
                  .getState()
                  .columns.slice(1)
                  .filter((item) => item.prop !== "actions");
                const data = [
                  columns.map((item) => `"${item.name}"`).join(","),
                ];
                for (const item of resData) {
                  const rowData = columns.map((col) => {
                    const value = item[col.prop] ?? "";
                    return `"${value}"`;
                  });
                  data.push(rowData.join(","));
                }
                downloadAsCSVFile(data.join("\n"), tViews("circuitsLabel"));
                delete query.export;
                setTableLoader(false);
              } else {
                setTableError(res.errorObject);
                setTableLoader(false);
              }
            },
          });
          tableStoreRef.current = tableStore;
          useTableSettings(
            LOCAL_STORAGE_LABELS.AD_USAGE_CIRCUITS_AVAILABILITY_TABLE,
            AD_USAGE_CIRCUITS_AVAILABILITY_TABLE_DEFAULT_TGL,
            columnsHook,
            tableStore,
          );
          setTableStore(tableStore);
        }
      } catch (error) {
        // Handle error
        if (error.name === "AbortError") return; // Ignore abort error
        // Only process if this is the latest request
        if (latestRequest.current !== requestId) return;
      }
    };

    runEffect();
  }, [props.site]);

  useEffect(() => {
    return () => {
      props.saveSelectedTrendCircuits([]);
    };
  }, []);

  return (
    <div
      className={css["ad-circuit-availability-table"]}
      data-cy="p-netw-ad-cs-table"
      data-testid="p-netw-ad-cs-table"
      aria-label={props.header}
    >
      <h2>{props.header}</h2>
      {tableStore && resReceived ? (
        <div>
          <div className="customt-timestamp">
            <CustomTimeStamp
              prevTime={props.selectedHour}
              currentTime={addUnitsFromDate(
                new Date(props.selectedHour),
                1,
                "h",
              )}
            />
          </div>
          <ADTableView
            tableStore={tableStore}
            header={tViews("summaryTitleCircuits")}
            columns={tableStore?.getState()?.columns}
            id={"ad-avail-table"}
            status={{ isLoading: tableLoader, error: tableError }}
            defaultSort={defaultSort}
            getSelectedRows={getSelectedRows}
            isPagination={true}
            entity={tViews("predictiveNetworks.circuitsSmall")}
            tableSettings={{
              localStorageName:
                LOCAL_STORAGE_LABELS.AD_USAGE_CIRCUITS_AVAILABILITY_TABLE,
              defaultToggles: AD_USAGE_CIRCUITS_AVAILABILITY_TABLE_DEFAULT_TGL,
              dataTestid: tableSettingsId,
            }}
          />
        </div>
      ) : tableLoader ? (
        <Spinner />
      ) : (
        tableError && (
          <ErrorComponent
            code={"500"}
            width={"110px"}
            className={"large-dashlet-error"}
          />
        )
      )}
    </div>
  );
};

CircuitTrendAvailTable.propTypes = {
  selectedHour: PropTypes.string,
  site: PropTypes.string,
  saveSelectedTrendCircuits: PropTypes.func,
  header: PropTypes.string,
};

export default CircuitTrendAvailTable;
